// ABOUTME: Social features service for leaderboards, sharing, and following
// ABOUTME: Manages social interactions and community engagement

import { createContext, useCallback, useContext, useMemo, useState } from 'react'

/**
 * Leaderboard entry
 */
export interface LeaderboardEntry {
  id: string
  userId: string
  displayName: string
  avatarUrl?: string
  score: number
  rank: number
  isCurrentUser?: boolean
  change?: number
}

/**
 * Leaderboard types
 */
export type LeaderboardType = 'xp' | 'challenges' | 'outfits' | 'streak'
export type LeaderboardPeriod = 'weekly' | 'monthly' | 'allTime'

/**
 * Shared outfit
 */
export interface SharedOutfit {
  id: string
  outfitId: string
  userId: string
  displayName: string
  title: string
  description?: string
  imageUrl?: string
  itemCount: number
  likes: number
  views: number
  shareToken: string
  createdAt: Date
  tags: string[]
}

/**
 * User follow relationship
 */
export interface FollowRelation {
  followerId: string
  followingId: string
  createdAt: Date
}

/**
 * Activity feed item
 */
export interface SocialActivity {
  id: string
  userId: string
  displayName: string
  avatarUrl?: string
  type: 'outfit_share' | 'achievement' | 'level_up' | 'challenge_complete'
  content: string
  metadata?: Record<string, string>
  timestamp: Date
}

/**
 * Mock leaderboard data
 */
const MOCK_LEADERBOARD: Record<LeaderboardType, LeaderboardEntry[]> = {
  xp: [
    { id: '1', userId: 'user-1', displayName: 'StyleMaster', score: 12450, rank: 1, change: 0 },
    { id: '2', userId: 'user-2', displayName: 'FashionKing', score: 11200, rank: 2, change: 1 },
    { id: '3', userId: 'user-3', displayName: 'DapperDan', score: 10890, rank: 3, change: -1 },
    { id: '4', userId: 'user-4', displayName: 'ClassicJohn', score: 9750, rank: 4, change: 2 },
    { id: '5', userId: 'user-5', displayName: 'ModernMike', score: 9200, rank: 5, change: 0 },
    { id: '6', userId: 'user-6', displayName: 'SharpDresser', score: 8900, rank: 6, change: -2 },
    { id: '7', userId: 'user-7', displayName: 'TailoredTom', score: 8500, rank: 7, change: 1 },
    { id: '8', userId: 'user-8', displayName: 'SmartStyle', score: 8100, rank: 8, change: 0 },
    { id: '9', userId: 'user-9', displayName: 'WardrobeWiz', score: 7800, rank: 9, change: 3 },
    { id: '10', userId: 'user-10', displayName: 'OutfitOscar', score: 7500, rank: 10, change: -1 },
  ],
  challenges: [
    { id: '1', userId: 'user-1', displayName: 'ChallengeChamp', score: 89, rank: 1, change: 0 },
    { id: '2', userId: 'user-3', displayName: 'QuestKing', score: 82, rank: 2, change: 2 },
    { id: '3', userId: 'user-2', displayName: 'TaskMaster', score: 78, rank: 3, change: -1 },
    { id: '4', userId: 'user-5', displayName: 'GoalGetter', score: 71, rank: 4, change: 0 },
    { id: '5', userId: 'user-4', displayName: 'AchieverAce', score: 68, rank: 5, change: 1 },
  ],
  outfits: [
    { id: '1', userId: 'user-2', displayName: 'OutfitOracle', score: 156, rank: 1, change: 1 },
    { id: '2', userId: 'user-1', displayName: 'LookLegend', score: 142, rank: 2, change: -1 },
    { id: '3', userId: 'user-4', displayName: 'StyleSage', score: 128, rank: 3, change: 0 },
    { id: '4', userId: 'user-6', displayName: 'ComboCreator', score: 115, rank: 4, change: 2 },
    { id: '5', userId: 'user-3', displayName: 'MixMaster', score: 103, rank: 5, change: -1 },
  ],
  streak: [
    { id: '1', userId: 'user-3', displayName: 'StreakStar', score: 45, rank: 1, change: 0 },
    { id: '2', userId: 'user-1', displayName: 'ConsistentCarl', score: 38, rank: 2, change: 0 },
    { id: '3', userId: 'user-5', displayName: 'DailyDave', score: 32, rank: 3, change: 1 },
    { id: '4', userId: 'user-2', displayName: 'RegularRob', score: 28, rank: 4, change: -1 },
    { id: '5', userId: 'user-4', displayName: 'SteadySteve', score: 21, rank: 5, change: 2 },
  ],
}

/**
 * Mock shared outfits
 */
const MOCK_SHARED_OUTFITS: SharedOutfit[] = [
  {
    id: 'share-1',
    outfitId: 'outfit-1',
    userId: 'user-1',
    displayName: 'StyleMaster',
    title: 'Smart Casual Friday',
    description: 'Perfect for the office with a relaxed vibe',
    itemCount: 4,
    likes: 234,
    views: 1520,
    shareToken: 'abc123',
    createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
    tags: ['smart-casual', 'office', 'friday'],
  },
  {
    id: 'share-2',
    outfitId: 'outfit-2',
    userId: 'user-2',
    displayName: 'FashionKing',
    title: 'Weekend Brunch Look',
    description: 'Comfortable yet put-together',
    itemCount: 5,
    likes: 189,
    views: 980,
    shareToken: 'def456',
    createdAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
    tags: ['casual', 'weekend', 'brunch'],
  },
  {
    id: 'share-3',
    outfitId: 'outfit-3',
    userId: 'user-3',
    displayName: 'DapperDan',
    title: 'Date Night Ensemble',
    description: 'Making a great impression',
    itemCount: 6,
    likes: 312,
    views: 2100,
    shareToken: 'ghi789',
    createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
    tags: ['date-night', 'formal', 'evening'],
  },
]

/**
 * Mock activity feed
 */
const MOCK_ACTIVITY: SocialActivity[] = [
  {
    id: 'act-1',
    userId: 'user-1',
    displayName: 'StyleMaster',
    type: 'level_up',
    content: 'reached Level 8!',
    timestamp: new Date(Date.now() - 1 * 60 * 60 * 1000),
  },
  {
    id: 'act-2',
    userId: 'user-2',
    displayName: 'FashionKing',
    type: 'outfit_share',
    content: 'shared a new outfit: Weekend Brunch Look',
    timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
  },
  {
    id: 'act-3',
    userId: 'user-3',
    displayName: 'DapperDan',
    type: 'achievement',
    content: 'unlocked "Style Streak" achievement',
    timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000),
  },
  {
    id: 'act-4',
    userId: 'user-4',
    displayName: 'ClassicJohn',
    type: 'challenge_complete',
    content: 'completed the Category Master challenge',
    timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000),
  },
]

/**
 * Social state
 */
interface SocialState {
  following: string[]
  followers: string[]
  likedOutfits: string[]
  sharedOutfits: string[]
}

/**
 * Social actions
 */
interface SocialActions {
  followUser: (userId: string) => void
  unfollowUser: (userId: string) => void
  isFollowing: (userId: string) => boolean
  likeOutfit: (outfitId: string) => void
  unlikeOutfit: (outfitId: string) => void
  hasLiked: (outfitId: string) => boolean
  shareOutfit: (outfitId: string) => void
  reset: () => void
}

type SocialContextValue = SocialState & SocialActions

const STORAGE_KEY = 'gentleman-social-store'

const initialState: SocialState = {
  following: [],
  followers: [],
  likedOutfits: [],
  sharedOutfits: [],
}

function loadPersistedState(): SocialState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      return { ...initialState, ...parsed }
    }
  } catch {}
  return initialState
}

function persistState(state: SocialState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {}
}

const SocialContext = createContext<SocialContextValue | null>(null)

export function SocialStoreProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<SocialState>(loadPersistedState)

  const update = useCallback((updater: (prev: SocialState) => SocialState) => {
    setState((prev) => {
      const next = updater(prev)
      persistState(next)
      return next
    })
  }, [])

  const followUser = useCallback(
    (userId: string) => {
      update((s) => {
        if (s.following.includes(userId)) return s
        return { ...s, following: [...s.following, userId] }
      })
    },
    [update]
  )

  const unfollowUser = useCallback(
    (userId: string) => {
      update((s) => ({ ...s, following: s.following.filter((id) => id !== userId) }))
    },
    [update]
  )

  const isFollowing = useCallback(
    (userId: string) => state.following.includes(userId),
    [state.following]
  )

  const likeOutfit = useCallback(
    (outfitId: string) => {
      update((s) => {
        if (s.likedOutfits.includes(outfitId)) return s
        return { ...s, likedOutfits: [...s.likedOutfits, outfitId] }
      })
    },
    [update]
  )

  const unlikeOutfit = useCallback(
    (outfitId: string) => {
      update((s) => ({ ...s, likedOutfits: s.likedOutfits.filter((id) => id !== outfitId) }))
    },
    [update]
  )

  const hasLiked = useCallback(
    (outfitId: string) => state.likedOutfits.includes(outfitId),
    [state.likedOutfits]
  )

  const shareOutfit = useCallback(
    (outfitId: string) => {
      update((s) => {
        if (s.sharedOutfits.includes(outfitId)) return s
        return { ...s, sharedOutfits: [...s.sharedOutfits, outfitId] }
      })
    },
    [update]
  )

  const reset = useCallback(() => {
    setState(initialState)
    persistState(initialState)
  }, [])

  const value = useMemo<SocialContextValue>(
    () => ({
      ...state,
      followUser,
      unfollowUser,
      isFollowing,
      likeOutfit,
      unlikeOutfit,
      hasLiked,
      shareOutfit,
      reset,
    }),
    [
      state,
      followUser,
      unfollowUser,
      isFollowing,
      likeOutfit,
      unlikeOutfit,
      hasLiked,
      shareOutfit,
      reset,
    ]
  )

  return <SocialContext.Provider value={value}>{children}</SocialContext.Provider>
}

export function useSocialStore(): SocialContextValue {
  const ctx = useContext(SocialContext)
  if (!ctx) throw new Error('useSocialStore must be used within SocialStoreProvider')
  return ctx
}

/**
 * Social service functions
 */
class SocialService {
  getLeaderboard(
    type: LeaderboardType,
    _period: LeaderboardPeriod = 'weekly',
    currentUserId?: string
  ): LeaderboardEntry[] {
    const data = [...MOCK_LEADERBOARD[type]]

    if (currentUserId) {
      const existingIndex = data.findIndex((e) => e.userId === currentUserId)
      if (existingIndex >= 0) {
        data[existingIndex].isCurrentUser = true
      } else {
        data.push({
          id: 'current',
          userId: currentUserId,
          displayName: 'You',
          score: Math.floor(Math.random() * 5000) + 1000,
          rank: Math.floor(Math.random() * 90) + 11,
          isCurrentUser: true,
          change: Math.floor(Math.random() * 10) - 5,
        })
      }
    }

    return data
  }

  getSharedOutfits(filter?: { tag?: string; sortBy?: 'recent' | 'popular' }): SharedOutfit[] {
    let outfits = [...MOCK_SHARED_OUTFITS]

    if (filter?.tag) {
      outfits = outfits.filter((o) => o.tags.includes(filter.tag!))
    }

    if (filter?.sortBy === 'popular') {
      outfits.sort((a, b) => b.likes - a.likes)
    } else {
      outfits.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
    }

    return outfits
  }

  getActivityFeed(followingOnly: boolean = false, followingIds: string[] = []): SocialActivity[] {
    let activities = [...MOCK_ACTIVITY]

    if (followingOnly && followingIds.length > 0) {
      activities = activities.filter((a) => followingIds.includes(a.userId))
    }

    return activities.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
  }

  generateShareLink(outfitId: string): string {
    const token = btoa(outfitId + '-' + Date.now())
    return `https://gentleman.app/outfit/${token}`
  }

  async copyShareLink(outfitId: string): Promise<boolean> {
    try {
      const link = this.generateShareLink(outfitId)
      await navigator.clipboard.writeText(link)
      return true
    } catch {
      return false
    }
  }
}

let socialServiceInstance: SocialService | null = null

export function getSocialService(): SocialService {
  if (!socialServiceInstance) {
    socialServiceInstance = new SocialService()
  }
  return socialServiceInstance
}

export { SocialService }
